import type { AppLocale } from "@/i18n/locales";

import type { CatalogueEntry, Claim, OpenQuestion, Specification } from "./catalogue";
import type { GatewayCatalogueEntry } from "./gateway-catalogue";

/**
 * Spanish overlays for catalogue entries that operators read in `es-AR`.
 *
 * English in `catalogue.ts` stays the technical source of truth (and what
 * tests assert). When the UI locale is Spanish, the detail page merges these
 * strings over the matching fields so chrome and body speak one language.
 */
type CatalogueOverlay = Partial<
  Pick<CatalogueEntry, "summary"> & {
    confirmed: readonly Specification[];
    claimed: readonly Claim[];
    platformBehaviour: readonly Specification[];
    toVerify: readonly OpenQuestion[];
  }
>;

const SEMAKIOT = "Listado Semakiot";
const MILESIGHT = "Hoja de datos Milesight";
const MILESIGHT_PAGE = "Página de producto Milesight";

const EM300_TH_ES: CatalogueOverlay = {
  summary:
    "Sensor ambiental compacto a batería que mide temperatura y humedad y reporta por LoRaWAN. Pensado para interior y exterior — oficinas, hospitales, fábricas, invernaderos — y la unidad piloto de este despliegue.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "EM300-TH" },
    {
      label: "Variante vendida localmente",
      value: "EM300-TH-915M, la versión de 915 MHz",
      source: SEMAKIOT,
    },
    { label: "Número de parte del revendedor", value: "MO20042", source: SEMAKIOT },
    { label: "Mide", value: "Temperatura y humedad" },
    { label: "Radio", value: "LoRaWAN" },
    { label: "Carcasa", value: "IP67, resistente a UV", source: SEMAKIOT },
    { label: "Alimentación", value: "Batería 4000 mAh, reemplazable", source: SEMAKIOT },
    {
      label: "Campo de temperatura decodificado",
      value: "temperature",
      source: "Ensayo en hardware, 2026-10-31",
    },
    {
      label: "Intervalo de reporte de las unidades ensayadas",
      value: "Cada 5 minutos",
      source: "Ensayo en hardware, 2026-10-31",
    },
  ],
  claimed: [
    {
      feature: "Elemento de medición Sensirion",
      soWhat:
        "Un proveedor creíble, pero el listado no da rango de medición ni cifra de exactitud, que es lo que decide si puede vigilar un congelador.",
    },
    {
      feature: "Hasta 10 km en línea de vista",
      soWhat:
        "Línea de vista. Un congelador es lo opuesto, así que tomalo como tope para campo abierto y nada más.",
    },
    {
      feature: "La batería dura varios años",
      soWhat:
        "Las unidades ensayadas reportan cada cinco minutos. El listado sigue sin convertir eso en una cifra de autonomía. Lo útil del claim es que la batería es reemplazable, así el piloto sigue siendo mantenible.",
    },
    {
      feature: "Guarda registros en local y los retransmite",
      soWhat:
        "Se pueden rellenar huecos después de una caída. También implica que una lectura retransmitida puede llegar con la hora equivocada — ver preguntas abiertas.",
    },
    {
      feature: "Configuración NFC",
      soWhat:
        "La puesta en marcha es en el dispositivo, en persona, con un teléfono. No hace falta gateway.",
    },
    {
      feature: "Funciona con gateways y network servers LoRaWAN estándar",
      soWhat:
        "The Things Stack debería andar, y el dispositivo no queda atado a la nube del fabricante.",
    },
    {
      feature: "Milesight D2D y Milesight IoT Cloud",
      soWhat: "Esta plataforma no los usa. Se listan para que nadie asuma que dependemos de ellos.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo de dispositivo a registrar", value: "Temperatura y humedad" },
    { label: "Cómo llegan las lecturas", value: "Webhook de The Things Stack, o el simulador" },
    {
      label: "Lecturas normalizadas",
      value: "Temperatura, humedad, batería, RSSI y SNR, cuando el uplink las trae",
    },
    {
      label: "Nombres de campos decodificados",
      value:
        "La temperatura se lee de temperature, confirmado en un uplink real. Humedad y batería siguen usando las listas de candidatas, sobreescribibles con LORAWAN_DECODED_FIELDS",
    },
    {
      label: "Intervalo de reporte",
      value: "5 minutos en las unidades ensayadas; se cuenta como sin reportar después de 15",
    },
    {
      label: "Umbrales de alerta",
      value: "Tomados del rango objetivo del activo al que está sujeto el dispositivo",
    },
    {
      label: "Uplinks duplicados",
      value: "Se descartan por contador de frame, así una retransmisión no se cuenta dos veces",
    },
    {
      label: "Hora de la medición",
      value: "Se infiere de la hora del gateway, porque no se confirmó un timestamp en el payload",
    },
  ],
  toVerify: [
    {
      topic: "Rango y exactitud de medición",
      why: "Decide si se puede confiar en él para un congelador a −20 °C. El listado nombra al proveedor del sensor pero no da cifras",
      answeredBy: MILESIGHT,
    },
    {
      topic: "Si el payload trae su propio timestamp",
      why: "El dispositivo bufferiza y reenvía registros, y un replay llega con la hora del gateway del replay. Sin timestamp en el payload, la excursión del martes se archiva como del jueves",
      answeredBy: "El codec, cuando esté disponible",
    },
    {
      topic: "Qué plan 915 MHz y qué sub-banda",
      why: "El número de parte dice 915 MHz, lo que acota, pero US915 y AU915 conviven ahí y la sub-banda tiene que coincidir con el gateway",
      answeredBy: "Milesight y Semakiot",
    },
    {
      topic: "IP67 en un entorno de lavado",
      why: "El grado está confirmado, pero un grado no es garantía frente a agua caliente y detergente",
      answeredBy: "Milesight y Semakiot",
    },
    {
      topic: "Comportamiento a baja temperatura",
      why: "La capacidad de la batería cae en el frío, y los ejemplos del listado son oficinas e invernaderos más que congeladores",
      answeredBy: "Hoja de datos, luego medido",
    },
    {
      topic: "Soporte de sonda externa",
      why: "Si el cuerpo del sensor puede quedar fuera del frío",
      answeredBy: MILESIGHT,
    },
    {
      topic: "Configuración remota",
      why: "NFC cubre la puesta en marcha en persona; cambiar el intervalo por aire es otra pregunta",
      answeredBy: MILESIGHT,
    },
    {
      topic: "Certificaciones, precio y disponibilidad",
      why: "Compromisos comerciales. El precio del listado es placeholder y el stock se contradice, así que ninguno se puede cotizar",
      answeredBy: "Semakiot",
    },
  ],
};

const EM410_RDL_ES: CatalogueOverlay = {
  summary:
    "Radar de onda milimétrica sin contacto que mide la distancia entre sí y la superficie de un líquido, y la reporta por LoRaWAN. Se monta arriba de lo que vigila y nunca lo toca, por eso sirve con contenidos peligrosos o corrosivos. El listado nombra cloacas, ríos, embalses y tanques de almacenamiento como usos previstos.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "EM410-RDL" },
    {
      label: "Variante vendida localmente",
      value: "EM410-RDL-915M, la versión de 915 MHz",
      source: SEMAKIOT,
    },
    { label: "Número de parte del revendedor", value: "MO08108", source: SEMAKIOT },
    { label: "Mide", value: "Distancia a la superficie de un líquido" },
    { label: "Radio", value: "LoRaWAN" },
    {
      label: "Rango de medición",
      value: "0,3 m a 12 m, con zona ciega cerca del sensor",
      source: SEMAKIOT,
    },
    { label: "Ángulo del haz de radar", value: "8°", source: SEMAKIOT },
    {
      label: "Carcasa",
      value: "IP68, cuerpo PVDF, apto para 1 m de agua dulce hasta 48 horas",
      source: SEMAKIOT,
    },
    {
      label: "Montaje",
      value:
        'Roscado, en G 1" o NPT 1", con antena interna o externa según el sufijo del número de parte',
      source: SEMAKIOT,
    },
  ],
  claimed: [
    {
      feature: "Radar de onda milimétrica, inmune a polvo, condensación, temperatura o ruido",
      soWhat:
        "La razón para elegirlo frente a uno ultrasónico: los modos de falla de un tanque — vapor sobre el líquido, condensación en el sensor — son los que este claim dice ignorar. No da cifra de exactitud, así que qué tan preciso mide sigue sin saberse.",
    },
    {
      feature:
        "Funciona con contenidos de alta salinidad, incluido agua de mar y salmuera concentrada",
      soWhat:
        "Amplía contra qué se podría vender la plataforma, pero el mismo listado no da exactitud, así que es un claim de supervivencia más que de precisión.",
    },
    {
      feature: "No invasivo: nunca toca el contenido",
      soWhat:
        "Permite sumar un sensor a un tanque ya en servicio, y que contenidos peligrosos no lo descarten. También implica que la lectura es una distancia, por eso esta plataforma guarda una distancia.",
    },
    {
      feature: "Acelerómetro de tres ejes integrado para inclinación",
      soWhat:
        "Un radar inclinado mide una distancia más larga y equivocada. Milesight expone la inclinación, pero esta plataforma hoy no ingiere ese campo, así que un sensor golpeado se leería como nivel en baja.",
    },
    {
      feature: "Alarma de zona ciega y calibración de curva de eco en un clic",
      soWhat:
        "Funciones del dispositivo, configuradas en el sensor. Nada en esta plataforma las controla, y la alarma de zona ciega no es uno de los incidentes que levantamos.",
    },
    {
      feature: "Almacenamiento local de historial con retransmisión",
      soWhat:
        "Se pueden rellenar huecos tras una caída — y, como con el EM300-TH, una lectura retransmitida puede llegar con la hora del replay. Ver preguntas abiertas.",
    },
    {
      feature: "Configuración NFC y Bluetooth",
      soWhat: "La puesta en marcha es en el dispositivo, en persona. No hace falta gateway.",
    },
    {
      feature: "Opción certificada ATEX/IECEx para atmósferas explosivas",
      soWhat:
        "Una opción en alguna variante, según el listado. Qué número de parte la trae, y si la de 915 MHz la trae, no está dicho y no debe repetirse a un cliente como si estuviera.",
    },
    {
      feature: "Milesight Development Platform, D2D y actualizaciones OTA",
      soWhat: "Esta plataforma no los usa. Se listan para que nadie asuma que dependemos de ellos.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo de dispositivo a registrar", value: "Distancia y nivel" },
    { label: "Cómo llegan las lecturas", value: "Webhook de The Things Stack, o el simulador" },
    {
      label: "Lecturas normalizadas",
      value: "Distancia, batería, RSSI y SNR, cuando el uplink las trae",
    },
    {
      label: "Unidad guardada y mostrada",
      value: "Milímetros en todos lados, sin conversión en ningún punto",
    },
    {
      label: "Qué se guarda",
      value:
        "La distancia del sensor al contenido, no un nivel. El nivel es profundidad del tanque menos distancia, y la plataforma no modela la geometría de montaje para calcularlo",
    },
    {
      label: "Umbrales de alerta",
      value:
        "Una distancia mínima y máxima en el activo, en milímetros. El mínimo es cuánto puede acercarse el contenido al sensor, así que cruzarlo significa demasiado lleno",
    },
    {
      label: "Incidentes que se levantan",
      value:
        "Se muestran como «Nivel bajo» cuando la distancia supera el máximo, y «Nivel alto» cuando baja del mínimo",
    },
    {
      label: "Nombres de campos decodificados",
      value: "Adivinados como `distance`, sobreescribibles con LORAWAN_DECODED_FIELDS",
    },
    {
      label: "Inclinación, alarmas de zona ciega y curvas de eco",
      value: "No se ingieren. La plataforma lee una distancia y nada más de este sensor",
    },
  ],
  toVerify: [
    {
      topic: "En qué unidad reporta la distancia el codec",
      why: "La desconocida más dañina acá. Guardamos milímetros; un codec que emita metros registraría 2 m como 2 mm y dejaría todo tanque permanentemente bajo el piso. El listado cotiza el rango en metros y no dice nada del payload",
      answeredBy: "Un uplink real, o el codec Milesight",
    },
    {
      topic: "Exactitud de medición",
      why: "Decide qué tan apretado vale poner un umbral. El listado afirma estabilidad e inmunidad a polvo y condensación pero no da ninguna cifra",
      answeredBy: MILESIGHT,
    },
    {
      topic: "Qué tan grande es en realidad la zona ciega",
      why: "El listado dice que hay una y la llama chica sin medirla. Es la región donde un tanque lleno deja de ser legible, así que decide dónde se puede montar el sensor",
      answeredBy: MILESIGHT,
    },
    {
      topic: "Qué envía el dispositivo cuando la lectura está en la zona ciega",
      why: "Si reporta un valor piso en vez de nada, un tanque demasiado lleno se vería como apenas lleno, y nuestra alerta de distancia mínima nunca dispararía",
      answeredBy: "El codec, cuando esté disponible",
    },
    {
      topic: "Qué números de parte traen la opción ATEX/IECEx",
      why: "El listado menciona la certificación sin atarla a una variante. Repetirla contra el número de parte equivocado sería un claim de seguridad que no podemos sostener",
      answeredBy: "Milesight y Semakiot",
    },
    {
      topic: "Si el payload trae su propio timestamp",
      why: "El mismo problema de retransmisión que el EM300-TH: un registro reenviado bajo la hora del replay pone el tanque vacío de anoche en el gráfico de esta mañana",
      answeredBy: "El codec, cuando esté disponible",
    },
    {
      topic: "Intervalo de reporte por defecto y vida de batería",
      why: "Define el umbral de offline. El listado no declara ninguno, y a diferencia del EM300-TH ni siquiera afirma una capacidad de batería",
      answeredBy: "Hoja de datos, luego medido en una unidad real",
    },
    {
      topic: "Qué plan 915 MHz y qué sub-banda",
      why: "US915 y AU915 viven en 915 MHz, y la sub-banda tiene que coincidir con el gateway",
      answeredBy: "Milesight y Semakiot",
    },
    {
      topic: "Si la inclinación se reporta en el uplink o solo se lee por NFC",
      why: "Un sensor golpeado mide más distancia, y esta plataforma lo mostraría como un tanque que se vacía. Si la inclinación viene en el payload, vale la pena ingerirla",
      answeredBy: "El codec, cuando esté disponible",
    },
    {
      topic: "Certificaciones, precio y disponibilidad",
      why: "Compromisos comerciales, y el precio del listado es placeholder. Nada de eso puede cotizarse",
      answeredBy: "Semakiot",
    },
  ],
};

const VS370_ES: CatalogueOverlay = {
  summary:
    "Radar de onda milimétrica y PIR que detecta si hay una persona en un ambiente, sin capturar imagen. El listado lo apunta a salas de reunión. Reporta un bit — ocupado o no — y lo que hace alerta es la hora más que la lectura: alguien a las 11 es el ambiente trabajando, alguien a las 3 de la mañana no. La comparación viene de las horas de ocupación esperada del activo; un sensor sobre un activo sin ellas se registra y nunca se juzga.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "VS370" },
    {
      label: "Variante vendida localmente",
      value: "VS370-915M, la versión de 915 MHz",
      source: SEMAKIOT,
    },
    { label: "Número de parte del revendedor", value: "MO20068", source: SEMAKIOT },
    { label: "Detecta", value: "Presencia humana y movimiento", source: SEMAKIOT },
    { label: "Radio", value: "LoRaWAN" },
    {
      label: "Elementos de sensado",
      value: "Radar de onda milimétrica combinado con PIR",
      source: SEMAKIOT,
    },
    {
      label: "Montaje",
      value: "Soporte magnético desmontable con 30° extra de deflexión",
      source: SEMAKIOT,
    },
    {
      label: "Rango de operación",
      value: "0 °C a +30 °C, así que no es cámara fría ni exterior",
      source: MILESIGHT,
    },
    {
      label: "Carcasa",
      value: "IP30, solo interior — sin protección contra agua",
      source: MILESIGHT,
    },
  ],
  claimed: [
    {
      feature: "No captura imágenes, así que se preserva la privacidad",
      soWhat:
        "El claim comercialmente importante: hace aceptable la presencia en un ambiente donde trabaja gente. También es la razón de que la lectura sea un solo bit, y de que esta plataforma guarde un bit y no un conteo de cabezas.",
    },
    {
      feature: "Detecta el menor movimiento y produce estadísticas de movilidad",
      soWhat:
        "Dos salidas distintas, y el listado no da cifras de ninguna. Si «estadísticas» son conteos, tiempo de permanencia u otra cosa, no se sabe, así que no se guarda nada de eso: solo el flag de ocupado, y un payload con un conteo aparece como un hueco en vez de un número al que le inventamos el significado.",
    },
    {
      feature: "Funciona con poca luz o en oscuridad total",
      soWhat: "Radar y PIR no necesitan luz. Saca toda una clase de restricción de despliegue.",
    },
    {
      feature: "Sensibilidad PIR y de radar configurable",
      soWhat:
        "El ajuste es en el dispositivo, no en esta plataforma. Un problema de falsos positivos se resuelve en el sensor.",
    },
    {
      feature: "Milesight D2D para control directo de luces y HVAC sin gateway",
      soWhat:
        "Una función genuinamente útil y del todo fuera de esta plataforma: el tráfico D2D nunca llega a un network server, así que no lo veríamos.",
    },
    {
      feature: "Períodos de hibernación personalizados para ahorrar batería",
      soWhat:
        "Relevante al umbral de offline. Un sensor que duerme a propósito no debe reportarse como uno que falló.",
    },
    {
      feature: "Configuración NFC y Bluetooth, OTA, Milesight Development Platform",
      soWhat:
        "La puesta en marcha y las actualizaciones ocurren fuera de esta plataforma, como con las demás unidades Milesight.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo de dispositivo a registrar", value: "Presencia" },
    {
      label: "Qué se guarda por lectura",
      value:
        "Ocupado o no, como un bit, más el nivel de batería. Sin conteo ni tiempo de permanencia",
    },
    {
      label: "Qué dispara una alerta",
      value:
        "Alguien presente durante toda la duración de la regla en un momento en que el activo dice que debería estar vacío. Diez minutos por defecto, que es lo que evita que un limpiador al pasar haga sonar un teléfono",
    },
    {
      label: "Sin horas de ocupación esperada en el activo",
      value:
        "Las lecturas se guardan y nunca se juzgan, igual que un congelador sin rango objetivo. Las horas se configuran en la página del activo",
    },
    {
      label: "Si el codec nunca reporta un ambiente vacío",
      value:
        "Un incidente abierto no se cerraría solo, porque nada lo contradiría. Es lo primero a chequear contra un uplink real",
    },
  ],
  toVerify: [
    {
      topic: "Rango de detección y ángulo de cobertura",
      why: "Decide cuántos sensores necesita un ambiente, y el listado no da cifra de ninguno",
      answeredBy: MILESIGHT,
    },
    {
      topic: "Qué trae realmente el payload, y bajo qué clave",
      why: "La plataforma lee un flag de ocupado de una lista corta de nombres candidatos, ninguno visto en un payload real de VS370. Un codec con otro nombre, o que reporte un conteo en vez de un flag, produce un uplink en dead-letter en vez de una lectura equivocada — visible, pero sigue siendo un dispositivo que no anda",
      answeredBy: "El codec, cuando esté disponible",
    },
    {
      topic: "Si reporta por cambio o por intervalo, y si reporta un ambiente vacío en absoluto",
      why: "Dos problemas en uno. Un sensor que solo habla cuando cambia el estado se ve idéntico a uno muerto, lo que rompe el barrido de offline. Y si nunca transmite el vacío, un incidente de presencia no tiene con qué resolverse y queda abierto hasta que alguien lo cierre a mano",
      answeredBy: "Hoja de datos Milesight, luego un uplink real",
    },
    {
      topic: "Fuente de alimentación y vida de batería",
      why: "El listado habla de ahorro de batería pero nunca dice qué lo alimenta. Sensores a red y a batería necesitan umbrales de offline distintos",
      answeredBy: MILESIGHT,
    },
    {
      topic: "Qué plan 915 MHz y qué sub-banda",
      why: "La misma pregunta que el resto: 915 MHz acota sin resolver",
      answeredBy: "Milesight y Semakiot",
    },
    {
      topic: "Si el modelo de ocupación esperada calza con cómo se usa el ambiente",
      why: "La plataforma juzga presencia contra una agenda semanal de días y horas, que calza con una oficina y con un depósito al que nadie debería entrar. Un espacio reservado ad hoc, o con una rotación que cambia semana a semana, no tiene agenda contra la cual comparar",
      answeredBy: "Semakiot, y el primer cliente que despliegue uno",
    },
    {
      topic: "Certificaciones, precio y disponibilidad",
      why: "Compromisos comerciales. Nada de eso puede cotizarse",
      answeredBy: "Semakiot",
    },
  ],
};

const EM300_SLD_ES: CatalogueOverlay = {
  summary:
    "Sensor puntual de fuga compacto con sonda de acero inoxidable que detecta líquido conductor (unos 5 mm en la punta) y reporta por LoRaWAN. La página de Milesight también describe el hermano de soga/zona EM300-ZLD — ese modelo no está en nuestro registro. La plataforma guarda un bit de fuga — mojado o seco — y abre un incidente cuando la sonda permanece mojada durante la duración de la regla. Semakiot lista otros EM300 en público pero no tenía página de EM300-SLD el 2026-10-23 — SKU AU915 y stock quedan por verificar en la orden de compra.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "EM300-SLD (sonda / puntual)" },
    {
      label: "Página de producto del fabricante",
      value: "milesight.com/iot/product/lorawan-sensor/em300-sld-zld",
      source: MILESIGHT_PAGE,
    },
    {
      label: "Planes regionales listados por Milesight",
      value: "Incluye AU915 y US915 entre otros",
      source: MILESIGHT_PAGE,
    },
    {
      label: "Detecta",
      value: "Líquido conductor en sonda puntual (disparo ~5 mm); cable típico 1,5 m",
      source: MILESIGHT_PAGE,
    },
    {
      label: "También mide",
      value: "Temperatura (−30 °C a +70 °C) y humedad",
      source: MILESIGHT_PAGE,
    },
    { label: "Radio", value: "LoRaWAN Class A", source: MILESIGHT_PAGE },
    {
      label: "Carcasa",
      value: "Transceptor IP67 resistente a UV; sonda de acero inoxidable",
      source: MILESIGHT_PAGE,
    },
    {
      label: "Alimentación",
      value: "ER18505 Li-SOCl2, 4000 mAh (8000 mAh opcional)",
      source: MILESIGHT,
    },
  ],
  claimed: [
    {
      feature: "Detección puntual de fuga para bases de caño, sótanos y fallas de bomba",
      soWhat:
        "Los casos de uso comerciales que nombra Milesight. Esta plataforma avisa por el bit; no localiza la fuga ni controla una bomba.",
    },
    {
      feature: "Temperatura y humedad integradas",
      soWhat:
        "Se decodifican cuando el codec las trae. La fuga es la métrica que dispara LEAK_DETECTED; las alertas de clima siguen usando las reglas de temperatura/humedad de la organización si esas lecturas llegan.",
    },
    {
      feature: "Configuración NFC y larga autonomía de batería",
      soWhat:
        "La puesta en marcha ocurre fuera de esta plataforma, como con las demás unidades Milesight EM300.",
    },
    {
      feature: "Opción de soga/zona EM300-ZLD en la misma página de producto",
      soWhat:
        "Otro SKU (cable sensor, no sonda puntual). No está registrado acá; no encargues ZLD esperando el comportamiento del SLD.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo de dispositivo a registrar", value: "Fuga de agua" },
    {
      label: "Qué se guarda por lectura",
      value: "Fuga o no como un bit, más batería; temperatura y humedad cuando vienen",
    },
    {
      label: "Qué dispara una alerta",
      value:
        "Toda lectura con fuga a lo largo de la duración de la regla dice mojado. El default sembrado es dos minutos en CRITICAL",
    },
    {
      label: "Si el codec nunca reporta seco",
      value:
        "Un incidente abierto no se cerraría solo, porque null no es evidencia. Verificá primero contra un uplink real",
    },
  ],
  toVerify: [
    {
      topic: "Listado Semakiot, SKU y stock AU915",
      why: "La página del fabricante está confirmada; la del revendedor local y el número de parte argentino no. La orden de compra debe nombrar AU915 (o el plan 915 correcto para Argentina) y EM300-SLD, no ZLD",
      answeredBy: "Semakiot",
    },
    {
      topic: "Clave del payload decodificado para el bit de fuga",
      why: "Candidatas: leakage / leak / water_leak. Milesight publica un decoder en la página de producto; ninguno verificado en una unidad real acá",
      answeredBy: "El codec, cuando esté disponible",
    },
    {
      topic: "Si el seco se reporta o solo se omite cuando está mojado",
      why: "El mismo riesgo de campo vacío que presencia en The Things Stack",
      answeredBy: "Un uplink real",
    },
    {
      topic: "Precio y plazo de entrega",
      why: "Comercial. Nada de eso puede cotizarse solo con precios de lista",
      answeredBy: "Semakiot",
    },
  ],
};

const EM300_MCS_ES: CatalogueOverlay = {
  summary:
    "Contacto magnético LoRaWAN que reporta si una puerta o ventana está abierta, más temperatura y humedad. Semakiot lista la variante 915M (SKU MO20043). La plataforma guarda un bit de contacto — abierta o cerrada — y abre un incidente cuando el contacto permanece abierto durante la duración de la regla. El precio público del listado Semakiot sigue siendo el placeholder; no lo cotices.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "EM300-MCS" },
    {
      label: "Listado Semakiot",
      value: "semakiot.com/productos/em300-mcs-915m-magnetic-contact-switch/",
      source: "Tienda Semakiot, 2026-10-23",
    },
    { label: "SKU Semakiot", value: "MO20043", source: "Schema de tienda Semakiot" },
    {
      label: "Detecta",
      value: "Contacto magnético abierto/cerrado (puerta o ventana)",
      source: MILESIGHT_PAGE,
    },
    {
      label: "También mide",
      value: "Temperatura y humedad",
      source: MILESIGHT_PAGE,
    },
    {
      label: "Radio",
      value: "LoRaWAN Class A, listado 915M",
      source: "Nombre del listado Semakiot",
    },
    {
      label: "Carcasa",
      value: "IP67 (familia Milesight EM300)",
      source: MILESIGHT_PAGE,
    },
    {
      label: "Campos de contacto decodificados",
      value: "door y magnet_status",
      source: "Ensayo en hardware, 2026-10-31",
    },
    {
      label: "Contacto cerrado en el uplink",
      value: "Se reporta, no se omite",
      source: "Ensayo en hardware, 2026-10-31",
    },
    {
      label: "Intervalo de reporte de las unidades ensayadas",
      value: "Cada 5 minutos",
      source: "Ensayo en hardware, 2026-10-31",
    },
  ],
  claimed: [
    {
      feature: "Puerta o ventana abierta/cerrada con uplink LoRaWAN",
      soWhat:
        "El uso comercial en cámaras y depósitos. Esta plataforma avisa cuando la abertura dura toda la regla; no cierra puertas ni despacha seguridad.",
    },
    {
      feature: "Temperatura y humedad integradas",
      soWhat:
        "Se decodifican cuando el codec las trae. La abertura de puerta es la métrica que dispara DOOR_OPEN; las alertas de clima siguen usando las reglas de temperatura/humedad de la organización si esas lecturas llegan.",
    },
    {
      feature: "Configuración NFC y larga autonomía de batería",
      soWhat:
        "La puesta en marcha ocurre fuera de esta plataforma, como con las demás unidades Milesight EM300.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo de dispositivo a registrar", value: "Contacto magnético" },
    {
      label: "Qué se guarda por lectura",
      value: "Abierta o cerrada como un bit, más batería; temperatura y humedad cuando vienen",
    },
    {
      label: "Qué dispara una alerta",
      value:
        "Toda lectura con contacto a lo largo de la duración de la regla dice abierta. El default sembrado es cinco minutos en WARNING",
    },
    {
      label: "Contacto cerrado",
      value:
        "Se reporta en el uplink. Un incidente puede resolverse cuando el contacto se cierra, porque un campo ausente no es cómo estas unidades dicen cerrada",
    },
    {
      label: "Intervalo de reporte",
      value: "5 minutos en las unidades ensayadas; se cuenta como sin reportar después de 15",
    },
  ],
  toVerify: [
    {
      topic: "Qué plan y sub-banda 915 MHz (AU915 para Argentina)",
      why: "El slug de Semakiot dice 915M; la orden de compra debe nombrar AU915 para Argentina",
      answeredBy: "Semakiot",
    },
    {
      topic: "Precio real y plazo de entrega",
      why: "El precio del listado es un placeholder. Nada de eso puede cotizarse",
      answeredBy: "Semakiot",
    },
  ],
};

const UC300_ES: CatalogueOverlay = {
  summary:
    "Controlador IoT industrial LoRaWAN con E/S digital y analógica, entradas PT100 y Modbus RS485. Semakiot lista la variante 915M (SKU MO20013). La plataforma guarda cada interfaz como un canal de dispositivo, no como una sola lectura de sensor. El precio público del listado Semakiot sigue siendo el placeholder; no lo cotices. Alimentación continua y cobertura Class C son requisitos de instalación.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "UC300" },
    {
      label: "Listado Semakiot",
      value: "semakiot.com/productos/uc300-915m/",
      source: "Tienda Semakiot, 2026-10-23",
    },
    { label: "SKU Semakiot", value: "MO20013", source: "Schema de tienda Semakiot" },
    {
      label: "Interfaces",
      value:
        "4× DI, 2× DO (relé), 2× 4–20 mA, 2× 0–10 V, 2× PT100, RS232, RS485 Modbus (hasta 32 canales)",
      source: MILESIGHT,
    },
    {
      label: "Radio",
      value: "LoRaWAN Class C, listado 915M (AU915 a confirmar en la OC)",
      source: "Hoja de datos Milesight / listado Semakiot",
    },
    {
      label: "Alimentación",
      value: "Terminal 5–24 VDC o USB-C 5 V — no va a batería",
      source: MILESIGHT,
    },
    {
      label: "Carcasa",
      value: "Metal IP30, interior, −20 °C a 60 °C",
      source: MILESIGHT,
    },
  ],
  claimed: [
    {
      feature: "Cableá sensores y actuadores diversos en un solo nodo LoRaWAN",
      soWhat:
        "Útil como puente para 4–20 mA, PT100, contactos secos y medidores Modbus. No es un sensor de heladera plug-and-play.",
    },
    {
      feature: "Lógica IF-THEN y acción autónoma cuando cae la red",
      soWhat:
        "Se configura en el dispositivo (ToolBox / Milesight Cloud), no en este admin. Esta plataforma ingiere uplinks; todavía no escribe reglas del UC300.",
    },
    {
      feature: "Control remoto de salida digital por downlink LoRaWAN",
      soWhat:
        "Milesight documenta el puerto 85. Este admin puede encolar abrir/cerrar un DO cuando hay credenciales TTS; sin ellas la acción queda auditada y se rechaza en el Network Server.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo de dispositivo a registrar", value: "Controlador IoT" },
    {
      label: "Qué se guarda por lectura",
      value:
        "Un ChannelReading por cada clave de interfaz presente en el payload decodificado (gpio_input_*, pt100_*, adc_*, adv_*, modbus_chn_*, …)",
    },
    {
      label: "Qué dispara una alerta",
      value:
        "Dispositivo offline, más reglas opt-in de rango o falla digital por canal configuradas en cada DeviceChannel (ADR-082)",
    },
    {
      label: "Supuesto del codec",
      value:
        "Claves del decoder oficial Milesight UC300. Sin verificar contra un uplink real de ZonaClara",
    },
  ],
  toVerify: [
    {
      topic: "Qué plan y sub-banda 915 MHz (AU915 para Argentina)",
      why: "El slug de Semakiot dice 915M; la orden de compra debe nombrar AU915 para Argentina",
      answeredBy: "Semakiot",
    },
    {
      topic: "Uplink TTS real de un UC300 en la aplicación piloto",
      why: "Confirma que el formateador de payload emite las claves oficiales que esta plataforma mapea",
      answeredBy: "Una unidad comisionada",
    },
    {
      topic: "Qué registros DI / AI / Modbus cablea realmente el sitio",
      why: "Las etiquetas de canal y las reglas de alerta futuras necesitan el significado del sitio, no solo la clave del codec",
      answeredBy: "La instalación / ZonaClara",
    },
    {
      topic: "Precio real y plazo de entrega",
      why: "El precio del listado es un placeholder. Nada de eso puede cotizarse",
      answeredBy: "Semakiot",
    },
  ],
};

const EM500_PT100_ES: CatalogueOverlay = {
  summary:
    "Sensor de temperatura LoRaWAN a batería con sonda PT100 externa. Semakiot lista la variante 915 MHz T200 (SKU MO20038), cuya sonda está especificada de -50 °C a 200 °C. El transmisor queda en aire ambiente; solo la sonda entra al caño, al horno o a la máquina. No mide humedad. El precio público del listado Semakiot sigue siendo el placeholder; no lo cotices.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "EM500-PT100-T200" },
    {
      label: "Variante vendida localmente",
      value: "EM500-PT100-915M-T200",
      source: "Listado Semakiot, 2026-10-28",
    },
    {
      label: "Número de parte del revendedor",
      value: "MO20038",
      source: "Schema de tienda Semakiot",
    },
    { label: "Mide", value: "Temperatura en una sonda PT100 externa. No humedad" },
    {
      label: "Rango de la sonda (T200)",
      value: "-50 °C a 200 °C, resolución 0,1 °C, 3 hilos, clase A, típico ±0,6 °C",
      source: MILESIGHT,
    },
    {
      label: "Sonda",
      value: "Acero inoxidable 304, 4×30 mm, IP67, cable de teflón de -200 °C a 290 °C",
      source: MILESIGHT,
    },
    {
      label: "Temperatura de trabajo del transmisor",
      value: "-30 °C a 70 °C. La caja no sigue a la sonda dentro del proceso",
      source: MILESIGHT,
    },
    {
      label: "Perfil de radio",
      value:
        "LoRaWAN Class A, Device Repository em500-pt100, hardware V1.x, firmware 2.x, profile-au915",
      source: "Device Repository de The Things Network",
    },
    {
      label: "Alimentación",
      value: "Batería ER34615 Li-SOCl2 de 19000 mAh, reemplazable",
      source: MILESIGHT,
    },
  ],
  claimed: [
    {
      feature: "Hasta 10 km en línea de vista",
      soWhat:
        "Línea de vista, según el listado de Semakiot. Un tablero metálico o una sala de planta no es campo abierto: tomalo como tope y relevá el sitio.",
    },
    {
      feature: "10 años con la batería de 19000 mAh",
      soWhat:
        "La cifra de Milesight es a intervalo de 10 minutos y 25 °C, en laboratorio. Esta plataforma asume un reporte cada cinco minutos y marca el equipo sin reportar a los quince, que es otro presupuesto de batería.",
    },
    {
      feature: "Guarda 1.000 registros y los retransmite",
      soWhat:
        "Se pueden rellenar huecos después de una caída. Una lectura retransmitida igual puede archivarse con la hora del gateway, porque esta plataforma no usa el timestamp de historial del payload.",
    },
    {
      feature: "Configuración por NFC y USB Type-C",
      soWhat:
        "La puesta en marcha es en el dispositivo, en persona. Cambiar el intervalo por el aire está documentado por Milesight y este admin no lo envía.",
    },
    {
      feature: "Milesight D2D y Milesight IoT Cloud",
      soWhat:
        "Esta plataforma no los usa. Se listan para que nadie asuma que el sensor tiene que vivir en la nube del fabricante.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo de dispositivo a registrar", value: "Temperatura" },
    { label: "Cómo llegan las lecturas", value: "Webhook de The Things Stack, o el simulador" },
    {
      label: "Lecturas normalizadas",
      value:
        "Temperatura y batería, más RSSI y SNR, cuando el uplink los trae. No se espera humedad",
    },
    {
      label: "Nombres de campos decodificados",
      value:
        "El decoder publicado de Milesight emite temperature y battery, que ya están en la lista de candidatos. Sin verificar contra un uplink de ZonaClara",
    },
    {
      label: "Intervalo de reporte asumido",
      value:
        "5 minutos, y el equipo cuenta como sin reportar a los 15. Es el default de la plataforma, no el intervalo de fábrica de este sensor",
    },
    {
      label: "Umbrales de alerta",
      value: "Salen del rango de temperatura objetivo del activo al que está pegado el equipo",
    },
    {
      label: "Hora de la medición",
      value:
        "Se infiere de la hora del gateway. Los marcos de historial del payload traen su propio timestamp y no se usan como hora de la lectura",
    },
  ],
  toVerify: [
    {
      topic: "Qué plan y sub-banda 915 MHz (AU915 para Argentina)",
      why: "El slug de Semakiot dice 915M. US915 y AU915 entran en ese nombre, y la orden de compra tiene que nombrar AU915 sub-banda 2",
      answeredBy: "Semakiot",
    },
    {
      topic: "Protección de la caja del transmisor",
      why: "El texto de la hoja de datos dice IP67. El campo ipCode del Device Repository dice IP66. La línea T200 de la hoja dice IP67 para la sonda",
      answeredBy: "Hoja de datos Milesight, contrastada con la unidad",
    },
    {
      topic: "Cómo se ve una sonda fallada o fuera de rango una vez decodificada",
      why: "El protocolo usa FFFF para falla de lectura y FFFD para fuera de rango. El decoder publicado los convierte en -0,1 °C y -0,3 °C, que esta plataforma no distingue de una lectura real",
      answeredBy: "Un uplink real",
    },
    {
      topic: "Si un historial retransmitido queda archivado con la hora correcta",
      why: "El decoder pone un timestamp unix en las entradas de historial. La ingesta sigue usando la hora del gateway para la lectura",
      answeredBy: "Un uplink real",
    },
    {
      topic: "Intervalo de reporte de fábrica",
      why: "El claim de batería asume diez minutos. La ventana de offline de acá asume cinco. Hay que leer la unidad",
      answeredBy: "La unidad, después Milesight",
    },
    {
      topic: "Largo de cable del T200 que entrega Semakiot",
      why: "La página de la serie dice 1,5 m y también que es personalizable. Hay que mirar la caja",
      answeredBy: "Semakiot",
    },
    {
      topic: "Registro RAMATEL",
      why: "Es un requisito legal para equipos en estas bandas, no un trámite menor",
      answeredBy: "Semakiot, o quien importa",
    },
    {
      topic: "Precio y disponibilidad",
      why: "El precio del listado es un placeholder. Nada de eso puede cotizarse",
      answeredBy: "Semakiot",
    },
  ],
};

const CT103_ES: CatalogueOverlay = {
  summary:
    "Una pinza que mide la corriente eficaz de un solo cable, hasta 250 A, y la reporta por LoRaWAN. Va adentro de un gabinete. No corta la carga y no es una factura de luz.",
  confirmed: [
    { label: "Fabricante", value: "Milesight" },
    { label: "Modelo", value: "CT103" },
    {
      label: "Variante para Argentina",
      value: "CT103-915M, la versión de 915 MHz y 250 A",
      source: MILESIGHT_PAGE,
    },
    { label: "Mide", value: "Corriente eficaz de un conductor" },
    { label: "Corriente nominal", value: "250 A", source: MILESIGHT },
    { label: "Orificio", value: "16 mm", source: MILESIGHT_PAGE },
    { label: "Radio", value: "LoRaWAN, clase A, AU915 entre los planes listados", source: MILESIGHT },
    { label: "Carcasa", value: "IP30", source: MILESIGHT },
    {
      label: "Campo de corriente decodificado",
      value: "current, en amperes",
      source: "Decoder CT103 de Milesight, script publicado",
    },
  ],
  claimed: [
    {
      feature: "Se alimenta del conductor, o con 5 V por USB-C",
      soWhat:
        "Por debajo de unos pocos amperes puede dejar de reportar si solo se alimenta del cable. La página de Milesight pone ese piso cerca de 3 A a un minuto y 2,8 A a diez minutos. Una página de revendedor cita otros pisos. El número queda a verificar.",
    },
    {
      feature: "NTC opcional en el cable, de −20 °C a 100 °C",
      soWhat:
        "Se guarda y se muestra cuando el uplink la trae. Es el cable, no el ambiente, y no abre un incidente de temperatura.",
    },
    {
      feature: "Amperio-hora acumulado",
      soWhat:
        "El decoder emite total_current. Esta plataforma lo deja en el payload crudo. No es una factura de luz.",
    },
  ],
  platformBehaviour: [
    { label: "Tipo al registrar", value: "Corriente de un cable" },
    {
      label: "Qué se guarda",
      value: "currentA en amperes, y temperatureC solo cuando el uplink la trae",
    },
    {
      label: "Qué abre una alerta",
      value:
        "Corriente fuera de la banda del circuito durante diez minutos. Sin banda no hay veredicto. La temperatura del cable no abre la alerta de ambiente.",
    },
    {
      label: "Supuesto de reporte",
      value:
        "La configuración arranca en 600 segundos, y el silencio espera 30 minutos. La lista igual marca desconectado a los 15.",
    },
    {
      label: "Supuesto del codec",
      value:
        "Clave oficial de Milesight `current`. Confirmada contra el decoder publicado, no contra una unidad de acá.",
    },
  ],
  toVerify: [
    {
      topic: "Listado Semakiot, SKU, stock y precio puesto en Argentina",
      why: "En este pase no se confirmó una página pública de Semakiot para el CT103. Nada de eso puede cotizarse",
      answeredBy: "Semakiot",
    },
    {
      topic: "Un uplink real en la aplicación piloto",
      why: "Confirma `current` y si una lectura fallida omite el campo",
      answeredBy: "Una unidad instalada",
    },
    {
      topic: "Corriente mínima que todavía alimenta una transmisión",
      why: "La página del fabricante y una de un revendedor no coinciden",
      answeredBy: "Milesight, y después una unidad",
    },
    {
      topic: "JoinEUI impreso en la unidad",
      why: "No hay un valor de fábrica registrado, así que el técnico carga el de la etiqueta",
      answeredBy: "La etiqueta",
    },
  ],
};

const OVERLAYS: Partial<Record<string, CatalogueOverlay>> = {
  "milesight-em300-th": EM300_TH_ES,
  "milesight-em500-pt100-t200": EM500_PT100_ES,
  "milesight-em410-rdl": EM410_RDL_ES,
  "milesight-vs370": VS370_ES,
  "milesight-em300-sld": EM300_SLD_ES,
  "milesight-em300-mcs": EM300_MCS_ES,
  "milesight-uc300": UC300_ES,
  "milesight-ct103": CT103_ES,
};

/** Merge locale copy onto a catalogue entry. English is the identity. */
export function localizeCatalogueEntry(entry: CatalogueEntry, locale: AppLocale): CatalogueEntry {
  if (locale !== "es-AR") {
    return entry;
  }

  const overlay = OVERLAYS[entry.id];
  if (!overlay) {
    return entry;
  }

  return {
    ...entry,
    ...(overlay.summary !== undefined ? { summary: overlay.summary } : {}),
    ...(overlay.confirmed !== undefined ? { confirmed: overlay.confirmed } : {}),
    ...(overlay.claimed !== undefined ? { claimed: overlay.claimed } : {}),
    ...(overlay.platformBehaviour !== undefined
      ? { platformBehaviour: overlay.platformBehaviour }
      : {}),
    ...(overlay.toVerify !== undefined ? { toVerify: overlay.toVerify } : {}),
  };
}

const GATEWAY_SUMMARIES_ES: Partial<Record<string, string>> = {
  "milesight-ug63":
    "Gateway LoRaWAN interior compacto de 8 canales. Semakiot lista el UG63-915M Ethernet. Sirve para un predio chico o como radio de relleno junto a un UG56/UG65/UG67 más grande. El plan piloto ZonaClara para Argentina es AU915-928 FSB 2 — confirmá que el SKU pedido coincida con ese plan.",
  "milesight-ug56":
    "Gateway industrial interior de 8 canales. Semakiot lista UG56-915M con backhaul Ethernet y Wi-Fi. La elección habitual cuando una radio debe llevar muchos sensores en un sitio o una huella compartida de manzana.",
  "milesight-ug67":
    "Gateway exterior IP67 de 8 canales con backhaul celular en el listado Semakiot (UG67-L08GL-915M). Para playones de tanques, cisternas rurales y sitios sin Ethernet confiable adentro. En la misma página también se describen Ethernet y Wi-Fi como opciones de backhaul.",
};

/** Gateway list rows only show the summary; overlay that string in Spanish. */
export function localizeGatewayCatalogueEntry(
  entry: GatewayCatalogueEntry,
  locale: AppLocale,
): GatewayCatalogueEntry {
  if (locale !== "es-AR") {
    return entry;
  }

  const summary = GATEWAY_SUMMARIES_ES[entry.id];
  return summary !== undefined ? { ...entry, summary } : entry;
}
